import { OpenImageViewerError } from '@/features/open-image-viewer'
import type {
  OpenImageViewerConfig,
  OpenImageViewerLoadedImage,
} from '@/features/open-image-viewer'
import type { Translations } from '@/shared/i18n'
import { readAvifDimensions, validateAvifDimensions } from './avifFile'

type ExportFormat = 'jpg' | 'png'
type AvifViewerCopy = Translations['avifViewer']

// Keep the 36 MP viewer limit, but avoid a second ~144 MB RGBA surface during
// synchronous canvas export at that boundary.
export const MAX_EXPORT_PIXELS = 16_000_000

const AVIF_PROBE =
  'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A='
let avifDecodeSupport: Promise<boolean> | null = null

export function resetAvifDecodeSupportForTests(): void {
  avifDecodeSupport = null
}

function supportsAvifDecode(): Promise<boolean> {
  if (!avifDecodeSupport) {
    avifDecodeSupport = new Promise((resolve) => {
      const image = new Image()
      image.onload = () => resolve(image.naturalWidth > 0 && image.naturalHeight > 0)
      image.onerror = () => resolve(false)
      image.src = AVIF_PROBE
    })
  }

  return avifDecodeSupport
}

export async function loadAvifImage(
  file: File,
  copy: AvifViewerCopy,
  config: OpenImageViewerConfig,
  signal?: AbortSignal,
): Promise<OpenImageViewerLoadedImage> {
  if (!(await supportsAvifDecode())) {
    throw new OpenImageViewerError(copy.errorsText.decode_failure, 'decode_failure')
  }

  const previewUrl = URL.createObjectURL(file)

  return new Promise((resolve, reject) => {
    const image = new Image()
    let settled = false
    const cleanup = (): void => {
      image.onload = null
      image.onerror = null
      signal?.removeEventListener('abort', abort)
    }
    const abort = (): void => {
      if (settled) return
      settled = true
      cleanup()
      image.src = ''
      URL.revokeObjectURL(previewUrl)
      reject(new DOMException('AVIF loading was aborted', 'AbortError'))
    }
    if (signal?.aborted) {
      abort()
      return
    }
    signal?.addEventListener('abort', abort, { once: true })
    void readAvifDimensions(file, config.maxPixels, signal).then((dimensions) => {
      if (signal?.aborted || settled) return
      if (dimensions?.kind === 'too_large') {
        settled = true
        cleanup()
        image.src = ''
        URL.revokeObjectURL(previewUrl)
        reject(new OpenImageViewerError(copy.errorsText.too_large_image, 'too_large_image'))
        return
      }
      if (dimensions.kind === 'invalid') {
        settled = true
        cleanup()
        image.src = ''
        URL.revokeObjectURL(previewUrl)
        reject(new OpenImageViewerError(copy.errorsText.corrupt, 'corrupt'))
        return
      }
      if (config.maxPixels && !validateAvifDimensions(dimensions.width, dimensions.height, config.maxPixels)) {
        settled = true
        cleanup()
        image.src = ''
        URL.revokeObjectURL(previewUrl)
        reject(new OpenImageViewerError(copy.errorsText.too_large_image, 'too_large_image'))
        return
      }
      image.src = previewUrl
    }).catch((error: unknown) => {
      if (settled) return
      if (error instanceof DOMException && error.name === 'AbortError') {
        abort()
        return
      }
      settled = true
      cleanup()
      image.src = ''
      URL.revokeObjectURL(previewUrl)
      reject(new OpenImageViewerError(copy.errorsText.corrupt, 'corrupt'))
    })
    image.onload = () => {
      if (settled) return
      settled = true
      cleanup()
      const width = image.naturalWidth
      const height = image.naturalHeight

      if (!width || !height) {
        image.src = ''
        URL.revokeObjectURL(previewUrl)
        reject(new OpenImageViewerError(copy.errorsText.corrupt, 'corrupt'))
        return
      }

      if (config.maxPixels && !validateAvifDimensions(width, height, config.maxPixels)) {
        image.src = ''
        URL.revokeObjectURL(previewUrl)
        reject(new OpenImageViewerError(copy.errorsText.too_large_image, 'too_large_image'))
        return
      }

      const canExport = width * height <= MAX_EXPORT_PIXELS
      if (!canExport) image.src = ''
      resolve({
        ...(canExport ? { element: image } : {}),
        previewUrl,
        width,
        height,
        mimeLabel: file.type || 'image/avif',
        formatLabel: config.formatLabel,
        revoke: canExport
          ? () => {
            image.src = ''
            URL.revokeObjectURL(previewUrl)
          }
          : () => URL.revokeObjectURL(previewUrl),
      })
    }
    image.onerror = () => {
      if (settled) return
      settled = true
      cleanup()
      image.src = ''
      URL.revokeObjectURL(previewUrl)
      reject(new OpenImageViewerError(copy.errorsText.corrupt, 'corrupt'))
    }
  })
}

export function encodeImage(
  image: HTMLImageElement,
  format: ExportFormat,
  copy: AvifViewerCopy,
  signal?: AbortSignal,
): Promise<Blob> {
  const mime = format === 'jpg' ? 'image/jpeg' : 'image/png'
  if (image.naturalWidth * image.naturalHeight > MAX_EXPORT_PIXELS) {
    return Promise.reject(new Error(copy.exportError))
  }
  const canvas = document.createElement('canvas')
  canvas.width = image.naturalWidth
  canvas.height = image.naturalHeight
  const releaseCanvas = (): void => {
    canvas.width = 0
    canvas.height = 0
  }
  const context = canvas.getContext('2d')

  if (!context) {
    releaseCanvas()
    return Promise.reject(new Error(copy.exportError))
  }

  if (format === 'jpg') {
    context.fillStyle = '#FFFFFF'
    context.fillRect(0, 0, canvas.width, canvas.height)
  }
  try {
    context.drawImage(image, 0, 0)
  } catch {
    releaseCanvas()
    return Promise.reject(new Error(copy.exportError))
  }

  return new Promise((resolve, reject) => {
    let settled = false
    const cleanup = (): void => {
      signal?.removeEventListener('abort', abort)
      releaseCanvas()
    }
    const fail = (error: Error): void => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }
    const abort = (): void => {
      fail(new DOMException('AVIF export was aborted', 'AbortError'))
    }
    if (signal?.aborted) {
      abort()
      return
    }
    signal?.addEventListener('abort', abort, { once: true })
    try {
      canvas.toBlob(
        (blob) => {
          if (settled) return
          if (!blob || blob.type !== mime) {
            fail(new Error(copy.exportError))
            return
          }
          settled = true
          cleanup()
          resolve(blob)
        },
        mime,
        format === 'jpg' ? 0.92 : undefined,
      )
    } catch {
      fail(new Error(copy.exportError))
    }
  })
}
